import type {
	ApiFormResponse,
	ApiFormBrandResponse,
	ApiBrandkitResponse,
	CreateSubmissionPayload,
	CreateSubmissionResponse,
	UpdateSubmissionPayload,
	VideoUploadResponse,
	AudioUploadResponse,
} from "../types/api.types";

const SERVICE_API_URL = import.meta.env.PROD
	? import.meta.env.VITE_SERVICE_CORE_URL || "http://localhost:8000"
	: "/api";
const SERVICE_EXTERNAL_URL = import.meta.env.PROD
	? import.meta.env.VITE_SERVICE_EXTERNAL_URL || "http://localhost:8080"
	: "/service-ext";

class ApiError extends Error {
	status: number;

	constructor(status: number, message: string) {
		super(message);
		this.status = status;
		this.name = "ApiError";
	}
}

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		const error = await response
			.json()
			.catch(() => ({ message: "Unknown error" }));
		throw new ApiError(
			response.status,
			error.message || `HTTP ${response.status}`,
		);
	}
	return response.json();
}

export const feedbackApi = {
	/**
	 * GET /forms/{form_slug}
	 * Load form configuration including pages, questions, and brandkit
	 */
	async getFormConfig(formSlug: string): Promise<ApiFormResponse> {
		const response = await fetch(
			`${SERVICE_EXTERNAL_URL}/forms/${formSlug}`,
		);
		return handleResponse<ApiFormResponse>(response);
	},

	/**
	 * GET /forms/{form_slug}/workspace
	 * Load form workspace details
	 */
	async getFormBrand(formSlug: string): Promise<ApiFormBrandResponse> {
		const response = await fetch(
			`${SERVICE_EXTERNAL_URL}/forms/${formSlug}/workspace`,
		);
		return handleResponse<ApiFormBrandResponse>(response);
	},

	/**
	 * GET /brandkit/{brand_slug}
	 * Load brandkit details
	 */
	async getBrandkit(brandSlug: string): Promise<ApiBrandkitResponse> {
		const response = await fetch(
			`${SERVICE_EXTERNAL_URL}/brandkit/${brandSlug}`,
		);
		return handleResponse<ApiBrandkitResponse>(response);
	},

	/**
	 * POST /external/forms/{form_slug}/submissions
	 * Create a new submission with initial rating
	 */
	async createSubmission(
		formSlug: string,
		payload: CreateSubmissionPayload,
	): Promise<CreateSubmissionResponse> {
		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formSlug}/submissions`,
			{
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
		);
		return handleResponse<CreateSubmissionResponse>(response);
	},

	/**
	 * PATCH /external/forms/{form_id}/{review_id}
	 * Update submission with additional data (user info, consent, etc.)
	 */
	async updateSubmission(
		formId: string,
		reviewId: string,
		payload: UpdateSubmissionPayload,
	): Promise<void> {
		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formId}/submissions/${reviewId}`,
			{
				method: "PATCH",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(payload),
			},
		);

		if (!response.ok) {
			const error = await response
				.json()
				.catch(() => ({ message: "Unknown error" }));
			throw new ApiError(
				response.status,
				error.message || `HTTP ${response.status}`,
			);
		}
	},

	/**
	 * PUT /external/forms/{formSlug}/submissions/{reviewId}/questions/{questionId}
	 * Submit text answer for a question
	 */
	async submitTextQuestion(
		formSlug: string,
		reviewId: string,
		questionId: string,
		contentText: string,
	): Promise<void> {
		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formSlug}/submissions/${reviewId}/questions/${questionId}`,
			{
				method: "PUT",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify({ content_text: contentText }),
			},
		);
		if (!response.ok) {
			const error = await response
				.json()
				.catch(() => ({ message: "Unknown error" }));
			throw new ApiError(
				response.status,
				error.message || `HTTP ${response.status}`,
			);
		}
	},

	/**
	 * PUT /external/forms/{formSlug}/submissions/{reviewId}/questions/{questionId}/video
	 * Request a Mux direct upload URL for a video question answer
	 */
	async uploadVideo(
		formSlug: string,
		reviewId: string,
		questionId: string,
	): Promise<VideoUploadResponse> {
		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formSlug}/submissions/${reviewId}/questions/${questionId}/video`,
			{
				method: "PUT",
				headers: { Accept: "application/json" },
			},
		);
		return handleResponse<VideoUploadResponse>(response);
	},

	/**
	 * PUT video file to Mux upload URL
	 */
	async uploadVideoToMux(uploadUrl: string, file: File): Promise<void> {
		const response = await fetch(uploadUrl, {
			method: "PUT",
			body: file,
		});
		if (!response.ok) {
			throw new ApiError(
				response.status,
				`Video upload failed: HTTP ${response.status}`,
			);
		}
	},

	/**
	 * POST /external/forms/{formSlug}/submissions/{reviewId}/questions/{questionId}/audio
	 * Upload audio file for a question answer
	 */
	async uploadAudio(
		formSlug: string,
		reviewId: string,
		questionId: string,
		file: File,
		audioDuration?: number,
	): Promise<AudioUploadResponse> {
		console.log("[uploadAudio] audioDuration:", audioDuration);
		const formData = new FormData();
		formData.append("audio", file);
		if (audioDuration) {
			formData.append("audio_duration", audioDuration.toString());
		}
		console.log("[uploadAudio] FormData entries:", [...formData.entries()].map(([k, v]) => `${k}=${v instanceof File ? v.name : v}`));

		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formSlug}/submissions/${reviewId}/questions/${questionId}/audio`,
			{
				method: "POST",
				headers: { Accept: "application/json" },
				body: formData,
			},
		);
		return handleResponse<AudioUploadResponse>(response);
	},

	/**
	 * POST /external/forms/{form_id}/submissions/{review_id}/commit
	 * Confirm that the form submission is complete
	 */
	async commitSubmission(formId: string, reviewId: string): Promise<void> {
		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formId}/submissions/${reviewId}/commit`,
			{
				method: "POST",
				headers: { Accept: "application/json" },
			},
		);
		if (!response.ok) {
			const error = await response
				.json()
				.catch(() => ({ message: "Unknown error" }));
			throw new ApiError(
				response.status,
				error.message || `HTTP ${response.status}`,
			);
		}
	},

	/**
	 * POST /external/forms/{form_id}/submissions/{review_id}/customer-avatar
	 * Upload customer avatar image
	 */
	async uploadCustomerAvatar(
		formId: string,
		reviewId: string,
		file: File,
	): Promise<void> {
		const formData = new FormData();
		formData.append("avatar", file);

		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formId}/submissions/${reviewId}/customer-avatar`,
			{
				method: "POST",
				headers: { Accept: "application/json" },
				body: formData,
			},
		);
		if (!response.ok) {
			const error = await response
				.json()
				.catch(() => ({ message: "Unknown error" }));
			throw new ApiError(
				response.status,
				error.message || `HTTP ${response.status}`,
			);
		}
	},

	/**
	 * POST /external/forms/{form_id}/submissions/{review_id}/customer-company-logo
	 * Upload customer company logo image
	 */
	async uploadCompanyLogo(
		formId: string,
		reviewId: string,
		file: File,
	): Promise<void> {
		const formData = new FormData();
		formData.append("logo", file);

		const response = await fetch(
			`${SERVICE_API_URL}/external/forms/${formId}/submissions/${reviewId}/customer-company-logo`,
			{
				method: "POST",
				headers: { Accept: "application/json" },
				body: formData,
			},
		);
		if (!response.ok) {
			const error = await response
				.json()
				.catch(() => ({ message: "Unknown error" }));
			throw new ApiError(
				response.status,
				error.message || `HTTP ${response.status}`,
			);
		}
	},
};

export { ApiError };
